import React, { useState, useEffect } from "react";
import { Layout, Modal, Tooltip, Button, Space, Checkbox } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { convertSchedulingEventsToCalender } from "../../../components/util/helpers";
import { useIntl } from "react-intl";
import Header from "./Header";
import SchedulingApproval from "../../../components/scheduling/SchedulingApproval";
import PatientDetails from "./PatientDetails";
import { loadPage } from "../schedulingSlice";
import moment from "moment";
import { useMedia } from "react-media";

const GLOBAL_MEDIA_QUERIES = {
  small: "(max-width: 899px)",
};

const ApprovalModal = (props) => {
  const {
    visible,
    onHide,
    currentDate,
    onNextMonth,
    onPreviousMonth,
    holidays,
    createEvent,
  } = props;
  const intl = useIntl();
  const [events, setEvents] = useState([]);
  const [originalEvents, setOriginalEvents] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [changes, setChanges] = useState({ approval: [], events: [] });
  const [quickEdit, setQuickEdit] = useState(false);
  const [showOk2goEvents, setShowOk2goEvents] = useState(true);

  const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );

  const patientId = useSelector(
    (state) => state.patientDetails.activePatient.id
  );
  const loadLoading = useSelector(
    (state) => state.pendingActions["scheduling/load"]
  );
  const { page: schedulingPage } = useSelector((state) => state.scheduling);
  const { items, effective_orders, order_time_extractions } = schedulingPage;
  const dispatch = useDispatch();

  useEffect(() => {
    convertItems();
  }, [items]);

  useEffect(() => {
    setFilteredOrders([]);
  }, [currentDate]);

  const convertItems = () => {
    const filtered = items.filter((i) => !(i?.is_day_center && !i?.approved));
    const convertedToCalender = convertSchedulingEventsToCalender(filtered);
    setEvents(convertedToCalender);
    setOriginalEvents(convertedToCalender);
  };

  const onAddSchedulingClick = () => {
    createEvent({ start: null, end: null, fromAprroval: true, currentDate });
  };

  const closeModal = () => {
    onHide();
    convertItems();
    setFilteredOrders([]);
    setQuickEdit(false);
  };

  const changeEvents = (arr) => {
    setEvents(arr);
  };

  const reload = async () => {
    const startDate = moment(currentDate).startOf("month");
    var endDate = moment(currentDate).endOf("month");
    if (patientId) {
      await dispatch(
        loadPage({
          query: {
            from: moment.utc(startDate).format(),
            to: moment.utc(endDate).format(),
          },
        })
      );
    }
  };

  const onFilterOrdersChanged = (eventId, val) => {
    const newFiltered = val
      ? filteredOrders.filter((item) => item !== eventId)
      : [...filteredOrders, eventId];
    setFilteredOrders(newFiltered);
  };

  const social_security = "ביטוח לאומי";
  const holocast_survivors_fund = "הקרן לנפגעי שואה";
  const ministry_of_defence = "משרד הביטחון";
  const ordersPalette = {
    [social_security]: "#53C31A",
    [holocast_survivors_fund]: "#F99646",
    [ministry_of_defence]: "#6C64FE",
  };

  const ordersBorderPalette = {
    [social_security]: "#C8D7C2",
    [holocast_survivors_fund]: "#F6BD8D",
    [ministry_of_defence]: "#D8D6FC",
  };

  const ordersBgPalette = {
    [social_security]: "#F1FBEE",
    [holocast_survivors_fund]: "#FDEEE0",
    [ministry_of_defence]: "#F2EFFF",
  };

  const setSchedulingChanges = (val) => {
    setChanges(val);
  };

  const onQuickEditChange = (e) => {
    setQuickEdit(e.target.checked);
  };

  const onOk2goFilterChange = (e) => {
    setShowOk2goEvents(e.target.checked);
  };

  const includedEvents = showOk2goEvents
    ? events
    : events.filter((event) => !event.ok2go_info?.id);

  const modalStyles = {
    body: {
      height: "100vh",
      maxWidth: "100vw",
      padding: "10px",
    },
  };

  return (
    <Modal
      width={"vw"}
      style={{
        height: "100vh",
        maxWidth: "100vw",
        position: "absolute",
        top: 0,
        left: 0,
      }}
      styles={modalStyles}
      className={"no-padding no-border-radius"}
      closable={false}
      open={visible}
      footer={null}
      destroyOnClose={true}
    >
      <Layout style={{ background: "transparent" }}>
        <Tooltip title="close">
          <Button
            onClick={closeModal}
            size="large"
            style={{ position: "absolute", right: 10, top: 10 }}
            shape="circle"
            icon={<CloseOutlined />}
          />
        </Tooltip>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "0px 0 10px 0",
          }}
        >
          <div style={{ display: "flex" }}>
            <Header
              currentDate={currentDate}
              effective_orders={effective_orders.filter(
                (j) =>
                  !j?.supporting_community && !(j?.is_day_center && j?.approved)
              )}
              order_time_extractions={order_time_extractions}
              ordersPalette={ordersPalette}
              onFilterOrdersChanged={onFilterOrdersChanged}
              onNextDate={onNextMonth}
              onPreviousDate={onPreviousMonth}
              events={events}
              showApproved={true}
              filteredOrders={filteredOrders}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              {matches.small ? null : (
                <div style={{ paddingBottom: "10px" }}>
                  <PatientDetails record={activePatient} />
                </div>
              )}
              <Space size="middle">
                <Checkbox
                  onChange={onOk2goFilterChange}
                  checked={showOk2goEvents}
                >
                  {intl.formatMessage({
                    id: "app.scheduling.ok2go_filter",
                  })}
                </Checkbox>
                <Checkbox onChange={onQuickEditChange}>
                  {intl.formatMessage({
                    id: "app.scheduling.quickSchedulingEdit",
                  })}
                </Checkbox>
                <Button
                  style={{ margin: "10px 0" }}
                  type="primary"
                  onClick={onAddSchedulingClick}
                  size="small"
                  disabled={
                    changes.approval.length > 0 || changes.events.length > 0
                  }
                >
                  <PlusOutlined />
                  {intl.formatMessage({
                    id: "app.scheduling.addNewScheduling",
                  })}
                </Button>
              </Space>
            </div>
          </div>
          <SchedulingApproval
            filteredOrders={filteredOrders}
            ordersPalette={ordersPalette}
            ordersBorderPalette={ordersBorderPalette}
            ordersBgPalette={ordersBgPalette}
            events={includedEvents}
            originalEvents={originalEvents}
            loadLoading={loadLoading}
            reload={reload}
            changeEvents={changeEvents}
            holidays={holidays}
            orders={effective_orders}
            setSchedulingChanges={setSchedulingChanges}
            quickEdit={quickEdit}
            currentDate={currentDate}
          />
        </div>
      </Layout>
    </Modal>
  );
};

export default ApprovalModal;
